import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PhoneCall, MagnifyingGlass, WaveSine, Robot, ArrowBendUpRight } from "@phosphor-icons/react";

const REFRESH_MS = 5000;
const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const fmtDur = (s) => {
  if (s === null || s === undefined) return "—";
  const sec = Math.max(0, Math.floor(s));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
};

const liveDuration = (startedAt, fallback, nowMs) => {
  if (!startedAt) return fallback ?? null;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return fallback ?? null;
  return Math.max(0, Math.round((nowMs - t) / 1000));
};

const CallToBadge = ({ state }) => {
  const transferred = state === "transferred";
  const Icon = transferred ? ArrowBendUpRight : Robot;
  return (
    <span
      data-testid="live-call-callto"
      data-state={transferred ? "transferred" : "bot"}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold " +
        (transferred
          ? "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]"
          : "bg-primary/10 text-primary")
      }
    >
      <Icon className="w-3.5 h-3.5" weight="bold" />
      {transferred ? "Transferred" : "Bot"}
    </span>
  );
};

export default function LiveCalls() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | bot | transferred
  const [now, setNow] = useState(Date.now());
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/live-calls");
      setRows(data.items || []);
    } catch {
      /* transient polling error — keep last snapshot */
    } finally {
      if (firstLoad.current) { setLoading(false); firstLoad.current = false; }
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  // 1s ticker so the Duration column counts up smoothly between refetches.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const isTransferred = (r) => r.call_state === "transferred";
  const botCount = useMemo(() => rows.filter((r) => !isTransferred(r)).length, [rows]);
  const transferredCount = useMemo(() => rows.filter(isTransferred).length, [rows]);

  const toggleFilter = (t) => setTypeFilter((cur) => (cur === t ? "all" : t));

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter === "bot" && isTransferred(r)) return false;
      if (typeFilter === "transferred" && !isTransferred(r)) return false;
      if (!s) return true;
      return [r.phone_number, r.callerid, r.client_id, r.client, r.campaign_id, r.campaign_name, r.campaign_prefix, r.lead_id, r.vicidial_ip, r.call_to]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s));
    });
  }, [rows, q, typeFilter]);

  return (
    <div data-testid="live-calls-page">
      <PageHeader
        title="Live Calls"
        subtitle="Calls currently connected to the voice bot — updates automatically every few seconds"
      >
        <span
          className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.1)] px-3 py-1.5 text-sm font-medium text-[hsl(var(--success))]"
          data-testid="live-calls-count"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))]" />
          </span>
          {rows.length} live
        </span>
      </PageHeader>

      {/* Two summary cards: click to filter the table by call type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <button
          type="button"
          data-testid="live-calls-card-bot"
          aria-pressed={typeFilter === "bot"}
          onClick={() => toggleFilter("bot")}
          className={
            "flex items-center gap-4 rounded-xl border bg-card card-shadow p-5 text-left transition-all duration-200 hover:-translate-y-0.5 " +
            (typeFilter === "bot" ? "border-primary ring-2 ring-primary/30" : "border-border")
          }
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Robot className="w-6 h-6" weight="duotone" />
          </span>
          <span>
            <span className="block text-sm text-muted-foreground">On Bot</span>
            <span className="block text-3xl font-bold tabular-nums" data-testid="live-calls-bot-count">{botCount}</span>
          </span>
        </button>

        <button
          type="button"
          data-testid="live-calls-card-transferred"
          aria-pressed={typeFilter === "transferred"}
          onClick={() => toggleFilter("transferred")}
          className={
            "flex items-center gap-4 rounded-xl border bg-card card-shadow p-5 text-left transition-all duration-200 hover:-translate-y-0.5 " +
            (typeFilter === "transferred" ? "border-[hsl(var(--warning))] ring-2 ring-[hsl(var(--warning)/0.3)]" : "border-border")
          }
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]">
            <ArrowBendUpRight className="w-6 h-6" weight="duotone" />
          </span>
          <span>
            <span className="block text-sm text-muted-foreground">Transferred</span>
            <span className="block text-3xl font-bold tabular-nums" data-testid="live-calls-transferred-count">{transferredCount}</span>
          </span>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="live-calls-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search phone, caller ID, client, campaign, lead ID, Call To or VICIdial IP"
            className="pl-9 rounded-lg"
          />
        </div>
        {/* Call-type filter */}
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" data-testid="live-calls-type-filter">
          {[
            { k: "all", label: "All" },
            { k: "bot", label: "Bot" },
            { k: "transferred", label: "Transferred" },
          ].map((o) => (
            <button
              key={o.k}
              type="button"
              data-testid={`live-calls-filter-${o.k}`}
              aria-pressed={typeFilter === o.k}
              onClick={() => setTypeFilter(o.k)}
              className={
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 " +
                (typeFilter === o.k ? "bg-card text-foreground card-shadow" : "text-muted-foreground hover:text-foreground")
              }
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading live calls…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={rows.length === 0 ? WaveSine : MagnifyingGlass}
          title={rows.length === 0 ? "No live calls right now" : "No matching live calls"}
          description={
            rows.length === 0
              ? "When your campaigns place calls, each connected call will appear here in real time."
              : "No live call matches your current filter or search. Try 'All' or a different search term."
          }
          testid="live-calls-empty"
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-calls-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                <TableHead>Caller ID</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Lead ID</TableHead>
                <TableHead>Call To</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>VICIdial IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.session_id || r.call_uuid} data-testid={`live-call-row-${r.session_id || r.call_uuid}`}>
                  <TableCell className="font-medium tabular-nums" data-testid="live-call-phone">{nn(r.phone_number)}</TableCell>
                  <TableCell className="tabular-nums" data-testid="live-call-callerid">{nn(r.callerid)}</TableCell>
                  <TableCell data-testid="live-call-client">{nn(r.client_id)}</TableCell>
                  <TableCell data-testid="live-call-customer">{nn(r.client)}</TableCell>
                  <TableCell data-testid="live-call-campaign">{nn(r.campaign_id)}</TableCell>
                  <TableCell className="tabular-nums" data-testid="live-call-lead-id">{nn(r.lead_id)}</TableCell>
                  <TableCell><CallToBadge state={r.call_state} /></TableCell>
                  <TableCell className="tabular-nums text-[hsl(var(--success))]" data-testid="live-call-duration">
                    {fmtDur(liveDuration(r.started_at, r.duration_sec, now))}
                  </TableCell>
                  <TableCell className="tabular-nums" data-testid="live-call-vicidial-ip">{nn(r.vicidial_ip)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
